import React from 'react';
import { motion } from 'framer-motion';
import { loadingVariants, pulseVariants } from '../../utils/animations';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext'; // Import context hook

/**
 * LoadingIndicator component
 * Shows loading states during the recommendation calculation,
 * displaying progress messages from the context.
 *
 * @param {Object} props - Component props (can add size/color back if needed)
 * @param {string} props.size - Spinner size ('sm', 'md', 'lg') - Optional
 * @param {string} props.color - Spinner color - Optional
 * @returns {JSX.Element} LoadingIndicator component
 */
const LoadingIndicator = ({
  size = 'md',
  color = 'purple'
}) => {
  // Get loading status from context
  const { loadingStatus } = useWirelessAdvisor();

  // Use messages from context or provide defaults
  const message = loadingStatus?.progressMessage || 'Calculating...';
  const currentAlgorithm = loadingStatus?.currentAlgorithm;

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'h-8 w-8';
      case 'lg': return 'h-20 w-20';
      case 'md': default: return 'h-16 w-16';
    }
  };

  // Get color classes
  const getColorClasses = () => {
    switch (color) {
      case 'blue': return 'border-blue-600';
      case 'pink': return 'border-pink-600';
      case 'green': return 'border-green-600';
      case 'purple': default: return 'border-purple-600';
    }
  };

  return (
    <motion.div
      className="flex flex-col items-center justify-center py-12 text-center" // Added text-center
      variants={loadingVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      {/* Spinner */}
      <motion.div
        className={`${getSizeClasses()} border-4 ${getColorClasses()} border-t-transparent rounded-full mb-6`}
        animate={{ rotate: 360 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
      />

      {/* Main Message (Progress Message) */}
      <motion.h2
        key={message} // Add key to re-trigger animation on message change
        className="text-xl font-semibold text-white mb-2" // Adjusted size
        variants={pulseVariants}
        initial="initial"
        animate="pulse"
      >
        {message}
      </motion.h2>

      {/* Current Algorithm (if available) */}
      {currentAlgorithm && (
        <motion.p
          key={currentAlgorithm} // Add key to re-trigger animation
          className="text-sm text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Algorithm: {currentAlgorithm}
        </motion.p>
      )}

      {/* Removed hardcoded steps and progress bar */}

    </motion.div>
  );
};

export default LoadingIndicator;